// MF-566 — Card 'latest-and-trend': "most recent" on the left, "trend" on the right, in ONE card (the PAT,
// NAPLAN and Essential Assessments blocks of the student dashboard). Rows come as one per (label, period):
// { label, period, sort, value, band, tone }. The card finds the latest period of every label and the change
// since the previous one, so which tests / domains / strands exist is decided by the data, never here.
import { useEffect, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import {
  SUMMARY_PALETTE,
  SERIES_COLORS,
  TONE_PILL,
  FONT,
  INK,
  baseTextStyle,
  legendBase,
  tooltipBase,
  fmtValue
} from '../boardTheme'
import { benchmarkColor } from '../boardPalettes'

/** rows → { labels: [{ label, points[], latest, previous }], periods: [ordered unique periods] } */
export const groupSeries = (rows = []) => {
  const periods = new Map() // period → sort
  const byLabel = new Map()
  for (const r of rows) {
    if (r.value === null || r.value === undefined) continue
    periods.set(r.period, r.sort)
    if (!byLabel.has(r.label)) byLabel.set(r.label, [])
    byLabel.get(r.label).push(r)
  }
  const labels = [...byLabel.entries()].map(([label, pts]) => {
    const points = [...pts].sort((a, b) => a.sort - b.sort)
    return {
      label,
      points,
      latest: points[points.length - 1],
      previous: points.length > 1 ? points[points.length - 2] : null
    }
  })
  return { labels, periods: [...periods.entries()].sort((a, b) => a[1] - b[1]).map(([p]) => p) }
}

// level (0 lowest .. 1 highest, e.g. a NAPLAN proficiency level) colours the pill on the orange-to-dark-blue
// scale so Strong and Exceeding look different; without it the pill keeps its tone colour.
const readableOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1B2430' : '#fff'
}

const Pill = ({ band, tone, level }) => {
  if (!band) return null
  const hasLevel = level !== null && level !== undefined && Number.isFinite(Number(level))
  const bg = hasLevel ? benchmarkColor(Number(level) * 100) : null
  const c = bg ? { bg, fg: readableOn(bg) } : TONE_PILL[tone] || TONE_PILL.neutral
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: '14px',
        fontSize: 11,
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.2,
        bgcolor: c.bg,
        color: c.fg,
        maxWidth: 110
      }}
    >
      {band}
    </Box>
  )
}

const Delta = ({ now, before, decimals }) => {
  if (before === null || before === undefined) return null
  const d = Number(now) - Number(before)
  const up = d > 0
  const color = d === 0 ? SUMMARY_PALETTE.faint : up ? SUMMARY_PALETTE.good : SUMMARY_PALETTE.bad
  return (
    <Typography component='span' sx={{ fontSize: 11, color, ml: 1 }}>
      {d === 0 ? '0' : `${up ? '▲' : '▼'} ${fmtValue(Math.abs(d), decimals)}`}
    </Typography>
  )
}

const LatestAndTrendCard = ({ data }) => {
  const opts = data?.meta?.card?.options || {}
  const decimals = opts.decimals || 0
  const { labels, periods } = useMemo(() => groupSeries(data?.rows || []), [data])

  const option = useMemo(
    () => ({
      textStyle: baseTextStyle,
      animationDuration: 400,
      tooltip: {
        ...tooltipBase,
        trigger: 'axis',
        valueFormatter: (v) => (v === null || v === undefined ? '–' : fmtValue(v, decimals))
      },
      legend: { ...legendBase, top: 0, data: labels.map((l) => l.label) },
      grid: { top: 36, left: 8, right: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: periods,
        axisLine: { lineStyle: { color: INK.axis } },
        axisTick: { show: false },
        axisLabel: { color: INK.secondary, fontSize: 11, fontFamily: FONT }
      },
      yAxis: {
        type: 'value',
        // zeroBased (PAT, NAPLAN): the scale starts at 0 like the design; otherwise it hugs the data
        ...(opts.zeroBased ? { min: 0 } : { scale: true }),
        splitLine: { lineStyle: { type: 'dashed', color: INK.grid } },
        axisLabel: { color: INK.muted, fontSize: 11, hideOverlap: true }
      },
      series: labels.map((l, i) => {
        const c = SERIES_COLORS[i % SERIES_COLORS.length]
        const byPeriod = new Map(l.points.map((p) => [p.period, p.value]))
        return {
          name: l.label,
          type: 'line',
          data: periods.map((p) => (byPeriod.has(p) ? byPeriod.get(p) : null)), // a missing period is a gap, never 0
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 2, color: c },
          itemStyle: { color: '#fff', borderColor: c, borderWidth: 2 }
        }
      })
    }),
    [labels, periods, decimals, opts.zeroBased]
  )

  // ECharts measures its box once: follow the tile when it is resized.
  const chartRef = useRef(null)
  const boxRef = useRef(null)
  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => chartRef.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, height: '100%', minHeight: 0 }}>
      <Box
        sx={{ flex: '1 1 240px', maxWidth: { sm: 360 }, minWidth: 0, overflowY: 'auto', maxHeight: '100%' }}
      >
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mb: 1 }}>{opts.leftTitle}</Typography>
        {labels.map((l, i) => (
          <Box
            key={l.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 1.25,
              borderBottom: i < labels.length - 1 ? `1px solid ${SUMMARY_PALETTE.grid}` : 'none'
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.ink, overflowWrap: 'anywhere' }}>
                {l.label}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 500, color: SUMMARY_PALETTE.good }}>
                {fmtValue(l.latest.value, decimals)}
                {opts.delta ? (
                  <Delta now={l.latest.value} before={l.previous?.value} decimals={decimals} />
                ) : null}
              </Typography>
            </Box>
            <Pill band={l.latest.band} tone={l.latest.tone} level={l.latest.level} />
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          flex: '2 1 320px',
          minWidth: 0,
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          pl: 2,
          borderLeft: `1px solid ${SUMMARY_PALETTE.grid}`
        }}
      >
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>{opts.rightTitle}</Typography>
        <div ref={boxRef} style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
          <ReactECharts ref={chartRef} option={option} notMerge style={{ height: '100%', width: '100%' }} />
        </div>
      </Box>
    </Box>
  )
}

export default LatestAndTrendCard
